import { useMemo, useState } from "react";
import { Instagram, Download, Camera, Bookmark, Circle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Tabbed "Share on socials" panel — Instagram post, story, Pinterest pin,
 * and a round "Profile bubble" (usable as an IG profile picture or story
 * highlight cover).
 *
 * Each tab exposes:
 *   - a live preview thumbnail
 *   - Download image (per-format JPG)
 *   - Copy caption (per-format optimised text with hashtags + link)
 *   - Open Instagram / Pinterest shortcut
 */

function buildCaption({ format, book }) {
  const title = book.title;
  const subtitle = book.subtitle ? ` — ${book.subtitle}` : "";
  const age = book.age_range || "";
  const when = book.publish_date ? `Coming ${book.publish_date}` : "Coming soon";
  const url = "mkbunchbooks.com";
  const author = "M.K. Bunch";

  if (format === "story") {
    return (
      `${title}${subtitle}\n` +
      `${when} · ${age}\n` +
      `by ${author}\n` +
      `↗ link in bio · ${url}`
    );
  }
  if (format === "pinterest") {
    return (
      `${title} — a whimsical new children's picture book by ${author}. ` +
      `${when}. ${age}. ` +
      `A joyful family read-aloud full of dancing, giggles, and warmth. ` +
      `Save this pin for the release. #picturebook #kidlit #readaloud #boardbook ` +
      `#childrensbooks #diverseKidLit #blackauthors\n` +
      `${url}`
    );
  }
  if (format === "bubble") {
    // caption for a profile-pic reveal / branding post
    return (
      `New look, same warmth 💗\n` +
      `MKBunchBooks — whimsical children's picture books by ${author}.\n` +
      `First release: ${title} — ${when}.\n` +
      `${url}`
    );
  }
  // default: post
  return (
    `🎉 ${title}${subtitle}\n` +
    `${when} · ${age}\n` +
    `by ${author}, from Washington, D.C.\n\n` +
    `A joyful, movement-loving read-aloud your little ones will ask for again and again.\n\n` +
    `#picturebook #kidlit #readaloud #boardbook #childrensbooks #diverseKidLit #blackauthors\n` +
    `${url}`
  );
}

function CopyCaptionButton({ text }) {
  const [copied, setCopied] = useState(false);
  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Caption copied — paste anywhere!");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed. Long-press the text to copy manually.");
    }
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-fredoka text-sm border transition-transform hover:-translate-y-0.5"
      style={{
        borderColor: copied ? "#5B8DEF" : "#F0D9E8",
        color: copied ? "#5B8DEF" : "#2D3055",
        background: "#FFFFFF",
      }}
      data-testid="book-share-copy-caption"
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
      {copied ? "Copied!" : "Copy caption"}
    </button>
  );
}

export default function BookShareSocials({ book }) {
  const options = useMemo(() => {
    const items = [];
    if (book.share_image_url) {
      items.push({ key: "post", label: "Instagram post", size: "1080 × 1080", icon: Instagram, url: book.share_image_url, aspect: "1 / 1" });
    }
    if (book.share_story_url) {
      items.push({ key: "story", label: "Instagram story", size: "1080 × 1920", icon: Camera, url: book.share_story_url, aspect: "9 / 16" });
    }
    if (book.share_pinterest_url) {
      items.push({ key: "pinterest", label: "Pinterest pin", size: "1000 × 1500", icon: Bookmark, url: book.share_pinterest_url, aspect: "2 / 3" });
    }
    if (book.share_bubble_url) {
      items.push({ key: "bubble", label: "Profile bubble", size: "1080 × 1080", icon: Circle, url: book.share_bubble_url, aspect: "1 / 1" });
    }
    return items;
  }, [book]);

  const [active, setActive] = useState(options[0]?.key);
  if (options.length === 0) return null;
  const activeItem = options.find((o) => o.key === active) || options[0];
  const filenameStem = book.title.replace(/[^\w]+/g, "-").toLowerCase().replace(/-+$/g, "");
  const caption = buildCaption({ format: activeItem.key, book });

  const externalHref =
    activeItem.key === "pinterest" ? "https://www.pinterest.com/" : "https://www.instagram.com/";
  const externalLabel =
    activeItem.key === "pinterest" ? "Open Pinterest" : "Open Instagram";

  return (
    <div className="mt-10" data-testid="book-share-section">
      <h3 className="font-fredoka text-2xl mb-4" style={{ color: "#2D3055" }}>Share on socials</h3>

      <div className="flex flex-wrap gap-2 mb-4" role="tablist">
        {options.map((o) => {
          const Icon = o.icon;
          const isActive = o.key === activeItem.key;
          return (
            <button
              key={o.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActive(o.key)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-fredoka text-sm transition-all"
              style={{
                background: isActive ? "#FF6B9D" : "#FFFFFF",
                color: isActive ? "#FFFFFF" : "#2D3055",
                border: `1.5px solid ${isActive ? "#FF6B9D" : "#F0D9E8"}`,
                boxShadow: isActive ? "0 8px 18px rgba(255,107,157,0.28)" : "none",
              }}
              data-testid={`book-share-tab-${o.key}`}
            >
              <Icon size={14} />
              {o.label}
            </button>
          );
        })}
      </div>

      <div
        className="rounded-3xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-stretch gap-5"
        style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #FFF3F8 100%)", border: "1px solid #F0EBFF" }}
      >
        <div
          className="rounded-2xl overflow-hidden shrink-0 bg-white"
          style={{ width: 168, aspectRatio: activeItem.aspect, boxShadow: "0 14px 34px rgba(45,48,85,0.12)" }}
        >
          <img
            src={activeItem.url}
            alt={`${book.title} — ${activeItem.label}`}
            className="w-full h-full object-cover"
            data-testid="book-share-preview"
          />
        </div>

        <div className="min-w-0 flex-1 flex flex-col">
          <p className="font-fredoka text-lg" style={{ color: "#2D3055" }}>
            Ready-to-post {activeItem.label.toLowerCase()} · {activeItem.size}
          </p>
          <p className="mt-1 text-sm font-nunito" style={{ color: "#4A4E6D" }}>
            Save the image and paste the caption — you&rsquo;re done.
          </p>

          <div
            className="mt-3 rounded-2xl p-3 text-sm font-nunito whitespace-pre-line"
            style={{
              background: "#FFFFFF",
              border: "1px dashed #F0D9E8",
              color: "#4A4E6D",
              maxHeight: 120,
              overflowY: "auto",
            }}
            data-testid="book-share-caption"
          >
            {caption}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href={activeItem.url}
              download={`${filenameStem}-${activeItem.key}.jpg`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-fredoka text-sm transition-transform hover:-translate-y-0.5"
              style={{ background: "#FF6B9D", color: "#FFFFFF", boxShadow: "0 8px 18px rgba(255,107,157,0.28)" }}
              data-testid="book-share-download"
            >
              <Download size={14} /> Download image
            </a>
            <CopyCaptionButton text={caption} />
            <a
              href={externalHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-fredoka text-sm border transition-transform hover:-translate-y-0.5"
              style={{ borderColor: "#F0D9E8", color: "#2D3055", background: "#FFFFFF" }}
              data-testid="book-share-external"
            >
              {activeItem.key === "pinterest" ? <Bookmark size={14} /> : <Instagram size={14} />}
              {externalLabel}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
